import { inject, Injectable } from '@angular/core';
import { EntityStateMap } from '@kerberos-compliance/compliance-shared-ts/common/enums/entity-state.enum';
import { TranslationEntityState } from '@kerberos-compliance/compliance-shared-ts/common/i18n/entity-state.translation';
import { isLiveContractLineItem } from '@kerberos-compliance/compliance-shared-ts/features/contract/utilities/contract-line-item-visibility.utility';
import type { ResponseWorkItemTypeDto } from '@kerberos-compliance/compliance-shared-ts/features/work-item/dtos/response.work-item-type.dtos';
import type { WorkItemTypeReferenceName } from '@kerberos-compliance/compliance-shared-ts/features/work-item/enums/work-item-type.enum';
import { getUserReadableNameWorkItemType } from '@kerberos-compliance/compliance-shared-ts/features/work-item/utilities/mappers-work-item-types';
import { DateFormatConfigurations, localizeDate, TranslationService } from '@kerberos-compliance/compliance-shared-ui';
import { EntityStateClassName, getEntityStateClassName } from '@shared/utils/entity-state-class.utility';
import dayjs from 'dayjs';
import type { ResponseContractDetailsDto, ResponseContractDetailsLineItemDto } from '../types/contracts.types';

const TranslationPrefix = 'organizations.detailPage.contractsTab';

// Validity-window states that no EntityState value expresses: a contract can still be Active yet sit outside
// its start/end dates, or - if its endDate was manually cleared - carry no validity window at all.
const ContractStateLabelKeys = {
  expired: `${TranslationPrefix}.contracts.state.expired`,
  invalid: `${TranslationPrefix}.contracts.state.invalid`,
  notYetStarted: `${TranslationPrefix}.contracts.state.notYetStarted`,
} as const;

const ContractValidityWindowClassName = {
  expired: EntityStateClassName[EntityStateMap.Expires],
  invalid: EntityStateClassName[EntityStateMap.Disabled],
  notYetStarted: EntityStateClassName[EntityStateMap.Draft],
} as const;

@Injectable()
export class ContractsDetailLabelsService {
  private readonly translationService = inject(TranslationService);

  /**
   * Formats the contract validity range (e.g. "01 Jan 25 – 31 Dec 25").
   *
   * @param {ResponseContractDetailsDto} contract - The contract.
   * @returns {string} The formatted date range.
   */
  public getContractDateRange(contract: ResponseContractDetailsDto): string {
    const language = this.translationService.activeLanguage();
    const start = localizeDate(contract.startDate, language, DateFormatConfigurations.dateFormat, false);
    const end = contract.endDate
      ? localizeDate(contract.endDate, language, DateFormatConfigurations.dateFormat, false)
      : '–';
    return `${start} – ${end}`;
  }

  /**
   * Whether a line item was soft-deleted and is therefore only visible because it was revealed.
   *
   * @param {ResponseContractDetailsLineItemDto} lineItem - The line item to check.
   * @returns {boolean} True when the line item is soft-deleted.
   */
  public isDeletedLineItem(lineItem: ResponseContractDetailsLineItemDto): boolean {
    return !isLiveContractLineItem(lineItem);
  }

  /**
   * Resolves the label for an inactive contract's state. A non-active state is named directly; an active
   * contract missing its endDate (only possible via a manual edit - Salesforce always computes one) is
   * reported as invalid rather than expired or not yet started, since it has no validity window to compare
   * against; otherwise it's reported as expired or not yet started, which no entity state expresses.
   *
   * @param {ResponseContractDetailsDto} contract - The contract to label.
   * @returns {string} The localized state label.
   */
  public getContractStateLabel(contract: ResponseContractDetailsDto): string {
    const language = this.translationService.activeLanguage();

    if (contract.state !== EntityStateMap.Active) {
      return TranslationEntityState[language][contract.state];
    }

    if (!contract.endDate) {
      return this.translationService.getTranslation(ContractStateLabelKeys.invalid);
    }

    const isBeforeValidityWindow = dayjs(contract.startDate).isAfter(dayjs(), 'day');
    return this.translationService.getTranslation(
      isBeforeValidityWindow ? ContractStateLabelKeys.notYetStarted : ContractStateLabelKeys.expired,
    );
  }

  /**
   * Resolves the status tag class for an inactive contract's state tag. Mirrors {@link getContractStateLabel}:
   * a non-active state reuses the shared entity-state colour mapping, while an active-but-out-of-window
   * contract gets its own colour instead of inheriting Active's `status-success` green.
   *
   * @param {ResponseContractDetailsDto} contract - The contract to label.
   * @returns {string} The `status-*` class for the contract's state.
   */
  public getContractStateClass(contract: ResponseContractDetailsDto): string {
    if (contract.state !== EntityStateMap.Active) {
      return getEntityStateClassName(contract.state);
    }

    if (!contract.endDate) {
      return ContractValidityWindowClassName.invalid;
    }

    const isBeforeValidityWindow = dayjs(contract.startDate).isAfter(dayjs(), 'day');
    return isBeforeValidityWindow
      ? ContractValidityWindowClassName.notYetStarted
      : ContractValidityWindowClassName.expired;
  }

  /**
   * Resolves the user-readable label for a work item type from its stable `referenceName`, falling back to the
   * backend `displayName` when no translation exists.
   *
   * @param {Pick<ResponseWorkItemTypeDto, 'referenceName' | 'displayName'>} workItemType - The work item type.
   * @returns {string} The translated label, or the backend display name as a fallback.
   */
  public getWorkItemTypeLabel(workItemType: Pick<ResponseWorkItemTypeDto, 'referenceName' | 'displayName'>): string {
    const language = this.translationService.activeLanguage();
    return (
      getUserReadableNameWorkItemType(workItemType.referenceName as WorkItemTypeReferenceName, language) ??
      workItemType.displayName
    );
  }
}
